// src/utils/tactic/homography.js
// ─────────────────────────────────────────────────────────────────────────
// Perspective bridge between the tactic board's LOGICAL map space and the
// pixels of the 2.5D map render (public/map.png).
//
// The playable field is a square in game space, but map.png is a 3D render, so
// that square lands on screen as a trapezoid. A homography is the exact map
// between the two: it keeps straight lines straight — so a vision polygon or a
// movement path computed in logical space can be projected vertex-by-vertex
// with no further correction — while reproducing the foreshortening.
//
// LOGICAL SPACE is the unit square. Mid lane is the (0,0)->(1,1) diagonal:
//
//        (0,1) top-left corner ────────── (1,1) RED base
//              │                    ·····      │
//              │              ·····            │      · = mid lane
//              │        ·····                  │
//        (0,0) BLUE base ───────────── (1,0) bottom-right corner
//
// All terrain, tokens, drawings and vision live in logical space, which is why
// swapping sides is a 180° rotation and nothing else has to know about it.
// ─────────────────────────────────────────────────────────────────────────

/** Solve a dense n×n system by Gauss-Jordan with partial pivoting. */
function solveLinearSystem(A, b) {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) return null; // degenerate corners
    [M[col], M[pivot]] = [M[pivot], M[col]];

    const p = M[col][col];
    for (let c = col; c <= n; c++) M[col][c] /= p;

    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col];
      if (!f) continue;
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row) => row[n]);
}

/**
 * Homography taking the four `src` points to the four `dst` points.
 * Returns a row-major 3×3 as a flat array of 9 (h22 fixed at 1), or null.
 */
export function solveHomography(src, dst) {
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i];
    const [u, v] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -x * u, -y * u]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -x * v, -y * v]);
    b.push(v);
  }
  const h = solveLinearSystem(A, b);
  if (!h) return null;
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

/** Project a point through a homography. */
export function applyHomography(H, x, y) {
  const w = H[6] * x + H[7] * y + H[8];
  if (!w) return [0, 0];
  return [
    (H[0] * x + H[1] * y + H[2]) / w,
    (H[3] * x + H[4] * y + H[5]) / w,
  ];
}

/** Invert a 3×3 (adjugate / determinant). Returns null if singular. */
export function invertHomography(H) {
  const [a, b, c, d, e, f, g, h, i] = H;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (!det || !Number.isFinite(det)) return null;

  return [
    A / det,
    (c * h - b * i) / det,
    (b * f - c * e) / det,
    B / det,
    (a * i - c * g) / det,
    (c * d - a * f) / det,
    C / det,
    (b * g - a * h) / det,
    (a * e - b * d) / det,
  ];
}

// Logical corners, in the order the calibration handles are stored.
export const LOGICAL_CORNERS = [
  [0, 0], // blueBase
  [0, 1], // topLeft
  [1, 1], // redBase
  [1, 0], // bottomRight
];

export const CORNER_KEYS = ["blueBase", "topLeft", "redBase", "bottomRight"];

export const CORNER_LABELS = {
  blueBase: "มุมฐานบลู",
  topLeft: "มุมบนซ้าย",
  redBase: "มุมฐานเรด",
  bottomRight: "มุมล่างขวา",
};

// Where the four field corners sit in map.png, as fractions of image width and
// height so the numbers survive any resize of the source render.
//
// SOLVED, NOT EYEBALLED. A coach marked every tower and buff camp on the map
// render; those give eleven pairs of features that the map's 180° symmetry says
// must have logical coordinates summing to (1,1). These corners are the least
// squares fit of that constraint, with the scale pinned by requiring the marked
// features to fill the unit square. Mean symmetry residual came down from 0.06
// to 0.02, and the outermost towers — which the previous fit placed at y = -0.05,
// outside the playable square entirely — now land inside it.
//
// Two of the corners fall outside the image (v > 1, u > 1). That is correct: the
// render is cropped, and the playable square's corners lie beyond its edges.
// The calibration UI clamps a DRAG to the image, so those two cannot be adjusted
// by hand without widening that clamp.
export const DEFAULT_CORNERS = {
  blueBase: [0.1550, 1.0918],
  topLeft: [0.1723, 0.0684],
  redBase: [0.7328, 0.0285],
  bottomRight: [1.1038, 0.9776],
};

/**
 * Build the projector used by every layer of the board.
 *
 * `toScreen`/`toLogical` work in NORMALISED image space (0..1 of width/height)
 * so callers can multiply by whatever pixel size they render at.
 *
 * `scaleAt` returns the local depth foreshortening (1 at the field centre),
 * which is what lets hero tokens shrink as they walk "away" from the camera.
 */
export function createProjector(corners = DEFAULT_CORNERS) {
  const dst = CORNER_KEYS.map((k) => corners?.[k] || DEFAULT_CORNERS[k]);
  const H = solveHomography(LOGICAL_CORNERS, dst) ||
    solveHomography(LOGICAL_CORNERS, CORNER_KEYS.map((k) => DEFAULT_CORNERS[k]));
  const Hinv = invertHomography(H);

  // Perspective denominator at the centre — the reference for depth scaling.
  const wCentre = H[6] * 0.5 + H[7] * 0.5 + H[8];

  return {
    H,
    Hinv,
    corners: CORNER_KEYS.reduce((acc, k, i) => ({ ...acc, [k]: dst[i] }), {}),

    toScreen(x, y) {
      return applyHomography(H, x, y);
    },

    toLogical(u, v) {
      if (!Hinv) return [0, 0];
      return applyHomography(Hinv, u, v);
    },

    /** Depth-driven size multiplier for a token standing at (x, y). */
    scaleAt(x, y) {
      const w = H[6] * x + H[7] * y + H[8];
      if (!w) return 1;
      const s = wCentre / w;
      // Clamp so a token near the far corner never becomes unclickable.
      return Math.max(0.55, Math.min(1.6, s));
    },
  };
}

/** Keep a logical point inside the playable square. */
export function clampLogical(x, y, pad = 0.004) {
  const lo = pad;
  const hi = 1 - pad;
  return [Math.max(lo, Math.min(hi, x)), Math.max(lo, Math.min(hi, y))];
}
